// =========================================================================
// Queue a move from the Hot float: Pay an approved address, or Move to
// Treasury (17 Oct 2026, steps 6 and 7 of the operator's flow).
//
// What this sheet does is small and the words say so: it asks for an amount
// and queues a row the desk builds into a transaction, cards by the
// destination's class, and offers in Approvals for a Ledger signature.
// Nothing moves on this sheet. It needs an open approval window — the
// operator saying he is at the desk — and offers to open one when there is
// none, because a move queued with nobody present would be sent at the next
// tap with figures nobody checked that day.
//
// On the design system (22 Oct 2026): one line per state; the refusal's
// figures in the server's own line; the explanation behind About.
// =========================================================================

import { useEffect, useReducer, useRef, useState } from 'react';
import { groupDigits } from '../../lib/custody-format';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X, Clock } from 'lucide-react';
import { custody as api, type PayoutSource } from '../../lib/api';
import { CLASS_WORD } from '../../lib/custody-format';
import { fmtTime } from '../../lib/format';
import { amountProblem, shortAddress } from '../../lib/custody-format';
import { StateLine, Pending, MoreDetails } from '../../components/kit';
import { moveRefusal, moveRefusalMore, windowRefusal } from './refusals';
import {
  moveSheetReducer, moveSheetIdle, fieldsLocked, sending, replayable, refusedAttempt, unresolvedOf, nextPayload, newPaymentBlocked, moveAllowance,
  flaggedReleaseOpen, saveUnresolved, loadUnresolved, clearUnresolved, storeStep, trackedKey, readStateOf, newPaymentReadiness, newlyRecorded, heldForPick, type MoveAttempt,
} from '../../lib/custody-screen';
import { useHeldChoice } from '../../lib/useHeldChoice';
import { HeldForField } from '../../components/HeldForField';
import { ReadFailed } from './ReadFailed';
import { FIELD, SELECT } from './field';

export interface MoveDestinationRow {
  id: number;
  /** The destination as it is said: the customer's name, or the desk's own wallet's word. */
  name: string;
  address: string;
  /** For a payout address: whether a payout has reached it (the first-payout cap, db/0084); null for the desk's own wallets. */
  /** The whitelist's stage (db/0098): 'approved' — only the test can be paid, and the amount is fixed to it. */
  stage?: 'not_approved' | 'approved' | 'test_sent' | 'whitelisted' | null;
  /** The test's figure, USDT (the caps row's; 10 from an older server). */
  testPaymentUsdt?: string;
}

/** The spans the window control offers, in minutes. */
export const WINDOW_CHOICES = [30, 60, 120, 240] as const;

/**
 * The open-window control, shared with the Flow screen: says whether the
 * desk is attended and until when, and opens a window of a chosen span.
 */
export function WindowControl({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient();
  const [minutes, setMinutes] = useState<number>(60);
  const q = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, refetchInterval: 30_000 });
  const m = useMutation({
    mutationFn: () => api.openApprovalWindow(minutes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['custody', 'approval-window'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
    },
  });
  const w = q.data?.window ?? null;
  return (
    <div className={`rounded-md ${compact ? '' : 'bg-tg-secondary p-3 lg:bg-panel-2'}`}>
      <div className="flex items-center gap-2 text-body">
        <Clock className={`h-3.5 w-3.5 shrink-0 ${w ? 'text-pos' : 'text-tg-hint'}`} />
        {q.isError ? (
          <ReadFailed what="the window" error={q.error} onRetry={() => void q.refetch()} pending={q.isFetching} />
        ) : !q.data ? (
          <Pending inline what="Reading…" />
        ) : w ? (
          <span>Desk attended until <span className="font-medium">{fmtTime(w.expiresAt)}</span> Dubai time.</span>
        ) : (
          <span className="text-tg-hint">Nobody at the desk — no window open.</span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <select className={SELECT} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
          {WINDOW_CHOICES.map((n) => <option key={n} value={n}>{n >= 60 ? `${n / 60} h` : `${n} min`}</option>)}
        </select>
        <button className="btn-secondary min-h-[44px] text-body" disabled={m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Opening…' : w ? 'Extend the window' : 'Open a window'}
        </button>
      </div>
      {m.isError && <StateLine tone="bad" className="mt-1" more={windowRefusal(m.error)}>Not opened.</StateLine>}
    </div>
  );
}

/** The failure, in the words it came with — behind Details, never the headline. */
const errorText = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** A press's key: a UUID where the browser has one, else time and chance — within the door's [A-Za-z0-9_-]{8,64}. */
function newRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** The source shown: the locked attempt's, else the form's. */
const shownFromOf = (locked: boolean, attempt: { from: PayoutSource } | null | undefined, from: PayoutSource): PayoutSource => (locked && attempt ? attempt.from : from);

export function MoveSheet({ open, onClose, title, verb, destination, about, sources = ['running_hot'], onRecorded }: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The button's word: "Pay" or "Move". */
  verb: string;
  destination: MoveDestinationRow | null;
  /** What this move is, behind About. */
  about: string;
  /** Where the money may leave from (lib/custody-screen.ts paySources): one, or a choice. The first is the default — Merchant Hot for a merchant. */
  sources?: readonly PayoutSource[];
  /** Told once per payment when it is seen recorded — by the door's answer, by asking the desk under its key, or by a void that found it recorded. */
  onRecorded?: (toWalletId: number) => void;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState<PayoutSource>(sources[0] ?? 'running_hot');
  // The attempt — one immutable snapshot under one key, scoped to its payee,
  // locked through sending and recovery, kept across a closing and a route
  // change (lib/custody-screen.ts: the move sheet; Screens' sources reviews, 23 Oct 2026).
  const [sheet, dispatch] = useReducer(moveSheetReducer, undefined, () => moveSheetIdle(newRequestId()));
  const destinationId = destination?.id ?? null;
  const win = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, enabled: open });
  // What a NEW payment is judged by, read before the press: the caps; from Flagged, the cards and the register too.
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, enabled: open, staleTime: 30_000, retry: false });
  const wantsFlagged = open && from === 'frozen';
  const approvals = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, enabled: wantsFlagged, staleTime: 10_000, retry: false });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, enabled: wantsFlagged, staleTime: 30_000, retry: false });
  // Out of Flagged, whose held money it is (db/0103 held_for): the one depositor pre-selected, his choice among several.
  const holders = useQuery({ queryKey: ['custody', 'flagged-held'], queryFn: api.flaggedHeld, enabled: wantsFlagged, staleTime: 10_000, retry: false });
  const [heldChoice, setHeldChoice, resetHeldChoice] = useHeldChoice(holders.data?.holders);
  const m = useMutation({
    mutationFn: (p: MoveAttempt) => api.proposeMove({ toWalletId: p.toWalletId, amount: p.amount, from: p.from, requestId: p.requestId, ...(p.heldFor ? { heldFor: p.heldFor } : {}) }),
    onMutate: (p) => dispatch({ type: 'submit', attempt: p }),
    // Each answer names the transmission it is for: an older payment's late answer changes nothing.
    // The door's answer carries the row's status (same_press included): a replay of a swept or failed row settles as that, not as "queued".
    onSuccess: (r, p) => {
      dispatch({ type: 'done', seq: p.seq, status: r.status ?? null, newId: newRequestId() });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'summary'] });
    },
    onError: (error, p) => dispatch({ type: 'failed', seq: p.seq, error, newId: newRequestId() }),
    // A refusal that names the stage (test_pending, test_not_received): the address book's stage moved (#4). A
    // recorded payment is told below, whichever way it is found — this answer can come before its commit.
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); },
  });
  // A payment seen recorded — the door's answer, the desk asked under its key, a void that found it recorded — is
  // told once per key, and the address book read again (Screens' re-review of 55ecf55, #2).
  const toldKey = useRef<string | null>(null);
  useEffect(() => {
    const to = newlyRecorded(sheet.attempt, toldKey.current);
    if (to === null) return;
    toldKey.current = sheet.attempt!.requestId;
    void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] });
    onRecorded?.(to);
  }, [sheet.attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  // Opening for a payee: its own unresolved attempt comes back (this sheet's or the store's); another payee's stays in the store.
  useEffect(() => {
    if (!open || destinationId === null) return;
    // The payee's own default source (pay sheet round 8, #1): the sheet stays mounted, so the first render's
    // sources[0] — Hot — was kept for a merchant whose default is Merchant Hot. A restored attempt overrides it below.
    setFrom(sources[0] ?? 'running_hot');
    dispatch({ type: 'open', destinationId, kept: loadUnresolved(destinationId), newId: newRequestId() });
  }, [open, destinationId]); // eslint-disable-line react-hooks/exhaustive-deps
  // The form follows a restored attempt (locked, it shows the snapshot anyway; this keeps the two from drifting).
  useEffect(() => {
    if (sheet.attempt && (sheet.attempt.state === 'pending' || sheet.attempt.state === 'uncertain')) { setAmount(sheet.attempt.amount); setFrom(sheet.attempt.from); }
  }, [sheet.attempt]);
  // Every change of the attempt: a tracked one is kept under ITS payee for the next opening; what the reducer said
  // to forget — a settlement, a void, a terminal refusal — is forgotten by the key it had. Closing, or moving to
  // another payee, forgets nothing (Screens' Astra review, 23 Oct 2026).
  // The deletions are applied by key and then acknowledged to the reducer, which keeps them owed until then —
  // two actions landing in one render cannot lose one (Screens' round 7, 23 Oct 2026).
  useEffect(() => {
    const step = storeStep(sheet);
    if (!step) return;
    for (const c of step.clear) clearUnresolved(c.destinationId, c.requestId);
    if (step.save) saveUnresolved(step.save.toWalletId, step.save);
    if (step.clear.length > 0) dispatch({ type: 'acked', requestIds: step.clear.map((c) => c.requestId) });
  }, [sheet]);
  // Closing: the box empties; a pending or uncertain attempt is kept (the store has it too), anything else forgotten.
  useEffect(() => {
    if (!open) { setAmount(''); setFrom(sources[0] ?? 'running_hot'); resetHeldChoice(); dispatch({ type: 'close', newId: newRequestId() }); m.reset(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // An unresolved press — pending (its answer may never come) or uncertain — asks the desk what became of its key,
  // every few seconds, until the desk says it is recorded (with the row's status) or it is sent again.
  const key = trackedKey(sheet);
  const byKey = useQuery({
    queryKey: ['custody', 'move-by-request', key], queryFn: () => api.moveByRequest(key!),
    enabled: open && key !== null, refetchInterval: 5_000, retry: false, staleTime: 0,
  });
  useEffect(() => {
    if (key !== null && byKey.data?.recorded) dispatch({ type: 'recorded', requestId: key, status: byKey.data.status, newId: newRequestId() });
  }, [key, byKey.data]);
  // "Change the payment": the desk closes the key first (db/0093) — recorded, and it shows as queued; voided, and
  // nothing under that key can be recorded any more, so the next press is a new payment under a new key.
  const voiding = useMutation({
    mutationFn: (requestId: string) => api.voidPress(requestId),
    onSuccess: (r, requestId) => {
      if (r.outcome === 'recorded') { dispatch({ type: 'recorded', requestId, status: r.status, newId: newRequestId() }); void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] }); }
      else { dispatch({ type: 'voided', requestId, newId: newRequestId() }); m.reset(); }
    },
  });

  if (!open || !destination) return null;
  const attempt = sheet.attempt;
  const locked = fieldsLocked(sheet);
  const pending = sending(sheet);
  const uncertain = replayable(sheet);
  const refused = refusedAttempt(sheet);
  const queued = attempt?.state === 'queued';
  // What shows while locked is the snapshot itself, never a later edit.
  // The whitelist's test (db/0098): to an approved address not yet tested, the amount IS the test — fixed, not typed.
  const testOnly = destination?.stage === 'approved' && shownFromOf(locked, attempt, from) !== 'treasury';
  const fixedAmount = testOnly ? (limits.data?.testPaymentUsdt ?? destination?.testPaymentUsdt ?? '10') : null;
  const shownAmount = locked && attempt ? attempt.amount : (fixedAmount ?? amount);
  const shownFrom = locked && attempt ? attempt.from : from;
  const problem = shownAmount.trim() === '' ? null : amountProblem(shownAmount);
  const windowOpen = !!win.data?.window;
  const readiness = newPaymentReadiness(shownFrom, { limits: readStateOf(limits), approvals: readStateOf(approvals), balances: readStateOf(balances) });
  const allowance = limits.data ? moveAllowance({ amount: shownAmount, from: shownFrom, limits: limits.data, stage: destination.stage ?? null }) : { ok: true as const };
  const releaseOpen = shownFrom === 'frozen' ? flaggedReleaseOpen(approvals.data?.approvals, balances.data?.wallets) : false;
  const edit = (next: () => void) => { if (locked) return; next(); dispatch({ type: 'edit', newId: newRequestId() }); };
  const held = heldForPick(holders.data?.holders, heldChoice);
  const press = () => m.mutate(nextPayload(sheet, { toWalletId: destination.id, amount: (fixedAmount ?? amount).trim(), from, heldFor: from === 'frozen' ? held.heldFor : null }));
  const retryReads = () => { void limits.refetch(); if (wantsFlagged) { void approvals.refetch(); void balances.refetch(); void holders.refetch(); } };
  const readsPending = limits.isFetching || approvals.isFetching || balances.isFetching;
  const blocked = newPaymentBlocked({ amount: shownAmount, windowOpen, attempt: attempt?.state ?? null, readiness: readiness.state, allowance, releaseOpen, heldForReady: shownFrom !== 'frozen' || held.ready });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-sm rounded-t-2xl bg-tg-bg p-4 shadow-xl sm:rounded-2xl lg:bg-panel">
        <header className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-title font-semibold">{title}</h2>
            <p className="text-meta text-tg-hint">
              To <span className="font-medium text-tg-text">{destination.name}</span>
              <span className="ml-1 font-mono">{shortAddress(destination.address, 10, 0)}</span>
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-tg-hint lg:min-h-[40px] lg:min-w-[40px]">
            <X className="h-4 w-4" />
          </button>
        </header>

        {queued && attempt?.outcome === 'sending' ? (
          <div className="space-y-3">
            <StateLine tone="warn" more="The desk has broadcast this payment and waits for the chain to confirm it; if the chain refuses it, the desk queues it again for your signature. The sheet keeps asking the desk until it is swept.">
              Sent — confirming on the chain.
            </StateLine>
            {byKey.isFetching && <Pending inline what="Asking the desk…" />}
            {/* A poll that fails after the broadcast: the last word stays above, the failure is said with its retry (Screens' round 7). */}
            {byKey.isError && !byKey.isFetching && (
              <ReadFailed what="what became of it" error={byKey.error} onRetry={() => void byKey.refetch()} pending={byKey.isFetching} />
            )}
            <div className="flex gap-2">
              <Link to="/custody/approvals" className="btn-secondary min-h-[44px] flex-1 text-center text-body" onClick={onClose}>Open Approvals</Link>
              <button className="btn-secondary min-h-[44px] text-body" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : queued && attempt?.outcome === 'sent' ? (
          <div className="space-y-3">
            <StateLine tone="good" more="The desk had recorded this press and has sent it already (broadcast, or swept): there is nothing to sign and nothing to press again.">
              Already sent — nothing to sign.
            </StateLine>
            <div className="flex gap-2">
              <Link to="/custody/approvals" className="btn-secondary min-h-[44px] flex-1 text-center text-body" onClick={onClose}>Open Approvals</Link>
              <button className="btn-secondary min-h-[44px] text-body" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : queued ? (
          <div className="space-y-3">
            <StateLine tone="good" more="The desk builds the transaction and offers it in Approvals for your Ledger. Nothing has moved yet.">
              Queued — sign it in Approvals.
            </StateLine>
            <div className="flex gap-2">
              <Link to="/custody/approvals" className="btn-primary min-h-[44px] flex-1 text-center text-body" onClick={onClose}>Open Approvals</Link>
              <button className="btn-secondary min-h-[44px] text-body" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <WindowControl />
            {sources.length > 1 ? (
              <label className="block">
                <span className="mb-1 block text-meta text-tg-hint">From</span>
                <select className={`${SELECT} w-full`} value={shownFrom} disabled={locked} onChange={(e) => edit(() => setFrom(e.target.value as PayoutSource))}>
                  {sources.map((s) => <option key={s} value={s}>{CLASS_WORD[s] ?? s}</option>)}
                </select>
              </label>
            ) : (
              <p className="text-meta text-tg-hint">From {CLASS_WORD[shownFrom] ?? shownFrom}</p>
            )}
            <label className="block">
              <span className="mb-1 block text-meta text-tg-hint">Amount, USDT</span>
              <input
                className={`${FIELD} w-full font-mono`}
                inputMode="decimal"
                placeholder="0.00"
                value={shownAmount}
                autoComplete="off"
                readOnly={locked || fixedAmount !== null}
                onChange={(e) => edit(() => setAmount(e.target.value))}
              />
              {problem && <StateLine tone="bad" className="mt-1">{problem}</StateLine>}
              {!locked && fixedAmount !== null && (
                <StateLine className="mt-1" more="Whitelisting includes a test: the first payment to a new address is exactly this, and nothing else goes until the customer confirms it and you press Received on Payout addresses.">
                  The first payment here is the {fixedAmount} USDT test.
                </StateLine>
              )}
            </label>
            {/* What a new payment is judged by: read, or said to be waiting or failed — a locked attempt needs none of it. */}
            {!locked && readiness.state === 'waiting' && <Pending inline what={`Reading ${readiness.what}…`} />}
            {!locked && readiness.state === 'failed' && (
              <ReadFailed what={readiness.what} error={limits.error ?? approvals.error ?? balances.error} onRetry={retryReads} pending={readsPending} />
            )}
            {shownFrom === 'frozen' && (
              <HeldForField holders={holders.data?.holders} failed={holders.isError} retry={() => void holders.refetch()} chosen={heldChoice}
                onChoose={(v) => edit(() => setHeldChoice(v))} locked={locked && attempt ? (attempt.heldFor ?? null) : undefined} selectClass={`${SELECT} w-full`} />
            )}
            {!locked && shownFrom === 'frozen' && releaseOpen === false && (
              <StateLine tone="warn" more="One release from Flagged at a time (the operator, 22 Oct 2026): a second is refused until the first is signed or completes.">
                Flagged releases one at a time — see <Link to="/custody/approvals" className="underline">Approvals</Link>.
              </StateLine>
            )}
            {!locked && releaseOpen === true && (
              <StateLine tone="warn" more="An open release from Flagged is already waiting for your Ledger. Sign it in Approvals, or let it expire, before queuing another.">
                A release is already waiting — <Link to="/custody/approvals" className="underline">sign it first</Link>.
              </StateLine>
            )}
            {!locked && !allowance.ok && <StateLine tone="warn" more="What the caps on Flow → Caps allow for this source today, counted before the press; the server checks the same.">{allowance.line}</StateLine>}
            {/* words:ok — the server's own line carries the figures (caps, the float's reading); the refusal keeps the source it was for */}
            {refused && <StateLine tone="bad" more={moveRefusalMore(refused.error)}>{moveRefusal(refused.error, refused.from)}</StateLine>}
            {pending && (
              <StateLine tone="warn" more="The press is on its way to the desk. Its answer settles it; nothing else is pressed meanwhile — a second press of this payment would be the same row, a different one must wait.">
                Sending — waiting for the desk's answer.
              </StateLine>
            )}
            {uncertain && attempt && (
              <StateLine tone="warn" more={`${attempt.error ? `${errorText(attempt.error)}. ` : ''}The desk may have recorded this payment. The sheet asks the desk what became of this press every few seconds and settles when it is recorded. Pressing again sends the same instructions under the same key: the desk answers with the row it made, or makes it once — never twice. Change the payment asks the desk to close this press first: recorded, it shows as queued; not recorded, nothing under its key can be recorded any more, and the changed payment goes under a new key.`}>
                Not confirmed — the payment may be recorded.
              </StateLine>
            )}
            {locked && byKey.isFetching && !voiding.isPending && <Pending inline what="Asking the desk what became of it…" />}
            {locked && byKey.isError && !byKey.isFetching && (
              <ReadFailed what="what became of the press" error={byKey.error} onRetry={() => void byKey.refetch()} pending={byKey.isFetching} />
            )}
            {uncertain && voiding.isError && <StateLine tone="bad" more={errorText(voiding.error)}>Couldn't close this press — try again.</StateLine>}
            {!windowOpen && win.data && !locked && (
              <StateLine tone="warn">Open a window first — nobody is at the desk.</StateLine>
            )}
            {uncertain ? (
              <div className="flex gap-2">
                {/* A replay of the same snapshot under the same key: not a new payment, so not judged as one. */}
                <button className="btn-primary min-h-[44px] flex-1 text-body" disabled={m.isPending || voiding.isPending} onClick={press}>
                  {m.isPending ? 'Sending…' : 'Send the same again'}
                </button>
                {/* A changed payment: the desk closes this key first, under the key's own lock, so nothing can slip in after. */}
                <button className="btn-secondary min-h-[44px] text-body" disabled={m.isPending || voiding.isPending} onClick={() => voiding.mutate(attempt!.requestId)}>
                  {voiding.isPending ? 'Closing this press…' : 'Change the payment'}
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button className="btn-primary min-h-[44px] flex-1 text-body" disabled={blocked || m.isPending} onClick={press}>
                  {pending || m.isPending ? 'Queuing…' : `${verb} ${shownAmount.trim() && !problem ? `${groupDigits(shownAmount.trim())} USDT` : ''}`.trim()}
                </button>
                <button className="btn-secondary min-h-[44px] text-body" onClick={onClose}>{pending ? 'Close' : 'Cancel'}</button>
              </div>
            )}
            <MoreDetails label="About this move">
              <p className="text-meta text-tg-hint">{about}</p>
            </MoreDetails>
          </div>
        )}
      </div>
    </div>
  );
}
